import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  ArrowLeft, RefreshCw, Mail, CheckCircle2, AlertCircle, 
  ExternalLink, Send, Eye, MousePointerClick, XCircle,
  Calendar, Clock, Plus, Trash2, Play, Zap, Building2,
  ChevronRight, Loader2, Paperclip, Edit3
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailCampaigns() {
  const [searchParams] = useSearchParams();
  const initialDeliverableId = searchParams.get('deliverable');
  
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Follow-up dialog state
  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [generatingFollowUp, setGeneratingFollowUp] = useState(false);
  const [schedulingFollowUp, setSchedulingFollowUp] = useState(false);
  const [followUpType, setFollowUpType] = useState("new");
  const [followUpSubject, setFollowUpSubject] = useState("");
  const [followUpBody, setFollowUpBody] = useState("");
  const [followUpScheduleDate, setFollowUpScheduleDate] = useState("");
  const [followUpScheduleTime, setFollowUpScheduleTime] = useState("");
  const [followUpAttachPdf, setFollowUpAttachPdf] = useState(false);

  const fetchCampaigns = async () => {
    try {
      // Get all emails and group by deliverable
      const [emailsRes, statsRes] = await Promise.all([
        axios.get(`${API}/email/all?limit=200`),
        axios.get(`${API}/email/stats`)
      ]);
      
      setStats(statsRes.data);
      
      // Group emails by deliverable_id and enrich with scheduled follow-ups
      const emailsByDeliverable = {};
      for (const email of emailsRes.data.logs || []) {
        const delivId = email.deliverable_id;
        if (!emailsByDeliverable[delivId]) {
          emailsByDeliverable[delivId] = {
            deliverable_id: delivId,
            company_name: email.company_name || "Unknown",
            url: email.url || "",
            emails: [],
            scheduled: []
          };
        }
        emailsByDeliverable[delivId].emails.push(email);
      }
      
      // Fetch scheduled follow-ups for each deliverable
      for (const delivId of Object.keys(emailsByDeliverable)) {
        try {
          const scheduledRes = await axios.get(`${API}/email/followup/scheduled/${delivId}`);
          emailsByDeliverable[delivId].scheduled = scheduledRes.data || [];
        } catch (e) {
          // Ignore errors for scheduled fetch
        }
      }
      
      // Convert to array and sort by most recent activity
      const campaignList = Object.values(emailsByDeliverable).sort((a, b) => {
        const aLatest = a.emails[0]?.sent_at || "";
        const bLatest = b.emails[0]?.sent_at || "";
        return bLatest.localeCompare(aLatest);
      });
      
      setCampaigns(campaignList);
    } catch (error) {
      toast.error("Failed to fetch campaigns");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchCampaigns();
  };

  const openFollowUpDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setShowFollowUpDialog(true);
    resetFollowUpForm();
  };

  const resetFollowUpForm = () => {
    setFollowUpSubject("");
    setFollowUpBody("");
    setFollowUpScheduleDate("");
    setFollowUpScheduleTime("");
    setFollowUpType("new");
    setFollowUpAttachPdf(false);
  };

  const handleGenerateFollowUp = async () => {
    if (!selectedCampaign) return;
    
    setGeneratingFollowUp(true);
    try {
      const followUpNumber = (selectedCampaign.emails?.length || 0) + 
        (selectedCampaign.scheduled?.filter(s => s.status === 'scheduled').length || 0) + 1;
      
      const response = await axios.post(`${API}/email/followup/generate`, {
        deliverable_id: selectedCampaign.deliverable_id,
        follow_up_type: followUpType,
        follow_up_number: followUpNumber
      });
      setFollowUpSubject(response.data.subject || "");
      setFollowUpBody(response.data.body || "");
      toast.success("Follow-up email generated!");
    } catch (error) {
      toast.error("Failed to generate follow-up email");
    } finally {
      setGeneratingFollowUp(false);
    }
  };

  const handleScheduleFollowUp = async () => {
    if (!selectedCampaign || !followUpSubject || !followUpBody) {
      toast.error("Please generate or write the follow-up email first");
      return;
    }
    if (!followUpScheduleDate || !followUpScheduleTime) {
      toast.error("Please select a date and time to schedule");
      return;
    }

    const recipientEmail = selectedCampaign.emails?.[0]?.to_email;
    if (!recipientEmail) {
      toast.error("No recipient email found for this campaign");
      return;
    }

    setSchedulingFollowUp(true);
    try {
      const scheduledDateTime = new Date(`${followUpScheduleDate}T${followUpScheduleTime}`);
      const followUpNumber = (selectedCampaign.emails?.length || 0) + 
        (selectedCampaign.scheduled?.filter(s => s.status === 'scheduled').length || 0) + 1;
      
      await axios.post(`${API}/email/followup/schedule`, {
        deliverable_id: selectedCampaign.deliverable_id,
        to_email: recipientEmail,
        subject: followUpSubject,
        body: followUpBody,
        scheduled_for: scheduledDateTime.toISOString(),
        follow_up_number: followUpNumber,
        attach_pdf: followUpAttachPdf
      });
      
      toast.success(`Follow-up scheduled for ${scheduledDateTime.toLocaleString()}`);
      setShowFollowUpDialog(false);
      resetFollowUpForm();
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to schedule follow-up");
    } finally {
      setSchedulingFollowUp(false);
    }
  };

  const handleCancelScheduled = async (scheduledId) => {
    try {
      await axios.delete(`${API}/email/followup/scheduled/${scheduledId}`);
      toast.success("Follow-up cancelled");
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to cancel follow-up");
    }
  };

  const handleSendScheduledNow = async (scheduledId) => {
    try {
      await axios.post(`${API}/email/followup/send-now/${scheduledId}`);
      toast.success("Follow-up email sent!");
      fetchCampaigns();
    } catch (error) {
      toast.error("Failed to send follow-up");
    }
  };

  const setQuickSchedule = (days) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    setFollowUpScheduleDate(date.toISOString().split('T')[0]);
    setFollowUpScheduleTime("09:00");
  };

  const getStatusBadge = (email) => {
    if (email.status === 'bounced') {
      return <Badge className="bg-[#fee2e2] text-[#dc2626] border-0"><XCircle className="w-3 h-3 mr-1" />Bounced</Badge>;
    }
    if (email.status === 'delivered') {
      return <Badge className="bg-[#dcfce7] text-[#166534] border-0"><CheckCircle2 className="w-3 h-3 mr-1" />Delivered</Badge>;
    }
    return <Badge className="bg-brand-tint text-brand-dark border-0"><Send className="w-3 h-3 mr-1" />Sent</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f3f4f6] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-4 border-brand border-t-transparent animate-spin mx-auto mb-4"></div>
          <p className="text-[#6b7280]">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f4f6]">
      {/* Header */}
      <header className="bg-white border-b border-[#e2e8f0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-brand hover:text-brand-dark transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span className="font-medium">Back to Home</span>
              </Link>
            </div>
            <Button 
              onClick={handleRefresh}
              disabled={refreshing}
              variant="outline"
              className="border-brand text-brand hover:bg-brand hover:text-white"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-brand-ink">Email Campaigns</h1>
          <p className="text-[#6b7280] mt-1">Manage outreach sequences and track engagement</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand-tint flex items-center justify-center">
                  <Send className="w-5 h-5 text-brand-dark" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-brand-ink">{stats.total_sent}</p>
                  <p className="text-xs text-[#6b7280]">Emails Sent</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#dcfce7] flex items-center justify-center">
                  <Eye className="w-5 h-5 text-[#166534]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-brand-ink">{stats.total_opened}</p>
                  <p className="text-xs text-[#6b7280]">Opened</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#f0f9ff] flex items-center justify-center">
                  <MousePointerClick className="w-5 h-5 text-[#0369a1]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-brand-ink">{stats.open_rate}%</p>
                  <p className="text-xs text-[#6b7280]">Open Rate</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#fef3c7] flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#92400e]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-brand-ink">{campaigns.length}</p>
                  <p className="text-xs text-[#6b7280]">Active Campaigns</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Campaigns List */}
        {campaigns.length === 0 ? (
          <Card className="p-12 bg-white text-center">
            <Mail className="w-12 h-12 text-[#d1d5db] mx-auto mb-4" />
            <p className="text-[#6b7280] mb-2">No email campaigns yet</p>
            <p className="text-sm text-[#9ca3af] mb-4">Send your first email from a company analysis to start a campaign</p>
            <Link to="/">
              <Button className="btn-primary">
                Analyze a Company
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="space-y-4">
            <Accordion type="single" collapsible defaultValue={initialDeliverableId || undefined} className="space-y-4">
              {campaigns.map((campaign, index) => {
                const totalEmails = campaign.emails.length;
                const openedEmails = campaign.emails.filter(e => e.opened).length;
                const scheduledCount = campaign.scheduled.filter(s => s.status === 'scheduled').length;
                const latestEmail = campaign.emails[0];
                
                return (
                  <AccordionItem 
                    key={campaign.deliverable_id} 
                    value={campaign.deliverable_id}
                    className="bg-white rounded-lg border border-[#e2e8f0] overflow-hidden"
                  >
                    <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-[#f8fafc]">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-[#f0f9ff] flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-brand" />
                          </div>
                          <div className="text-left">
                            <h3 className="font-semibold text-brand-ink">{campaign.company_name}</h3>
                            {campaign.url && (
                              <p className="text-xs text-[#6b7280]">{new URL(campaign.url).hostname}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-sm font-medium text-[#374151]">{totalEmails} sent</p>
                            <p className="text-xs text-[#6b7280]">{openedEmails} opened</p>
                          </div>
                          {scheduledCount > 0 && (
                            <Badge className="bg-[#fef3c7] text-[#92400e] border-0">
                              <Clock className="w-3 h-3 mr-1" />
                              {scheduledCount} scheduled
                            </Badge>
                          )}
                          {latestEmail?.opened && (
                            <Badge className="bg-[#dcfce7] text-[#166534] border-0">
                              <Eye className="w-3 h-3 mr-1" />
                              Engaged
                            </Badge>
                          )}
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      {/* Campaign Actions */}
                      <div className="flex items-center justify-between mb-6 pt-2 border-t border-[#e2e8f0]">
                        <div className="flex items-center gap-2">
                          <Link to={`/results/${campaign.deliverable_id}`}>
                            <Button variant="outline" size="sm">
                              <ExternalLink className="w-4 h-4 mr-1" />
                              View Report
                            </Button>
                          </Link>
                        </div>
                        <Button 
                          onClick={() => openFollowUpDialog(campaign)}
                          size="sm"
                          className="bg-brand hover:bg-brand-dark text-white"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Schedule Follow-up
                        </Button>
                      </div>

                      {/* Email Timeline */}
                      <div className="space-y-4">
                        <h4 className="text-sm font-semibold text-[#374151]">Email Sequence</h4>
                        
                        {/* Sent Emails */}
                        {campaign.emails.map((email, emailIndex) => (
                          <div 
                            key={email.id}
                            className="relative pl-8 pb-4 border-l-2 border-[#e2e8f0] last:border-l-transparent"
                          >
                            <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-white border-2 border-brand flex items-center justify-center">
                              {email.opened ? (
                                <div className="w-2 h-2 rounded-full bg-[#22c55e]"></div>
                              ) : (
                                <div className="w-2 h-2 rounded-full bg-brand"></div>
                              )}
                            </div>
                            <div className="bg-[#f8fafc] rounded-lg p-4 border border-[#e2e8f0]">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs text-[#6b7280]">
                                      {emailIndex === 0 ? "Initial Email" : `Follow-up #${emailIndex}`}
                                    </span>
                                    {getStatusBadge(email)}
                                    {email.opened && (
                                      <Badge className="bg-[#dcfce7] text-[#166534] border-0 text-xs">
                                        <Eye className="w-3 h-3 mr-1" />
                                        Opened{email.open_count > 1 ? ` ${email.open_count}x` : ''}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="font-medium text-[#374151] text-sm">{email.subject}</p>
                                  <p className="text-xs text-[#6b7280] mt-1">
                                    To: {email.to_email} • {new Date(email.sent_at).toLocaleString()}
                                  </p>
                                  {email.opened && email.opened_at && (
                                    <p className="text-xs text-[#22c55e] mt-1">
                                      First opened: {new Date(email.opened_at).toLocaleString()}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* Scheduled Follow-ups */}
                        {campaign.scheduled.filter(s => s.status === 'scheduled').map((scheduled, schedIndex) => (
                          <div 
                            key={scheduled.id}
                            className="relative pl-8 pb-4 border-l-2 border-dashed border-[#fcd34d]"
                          >
                            <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-[#fef3c7] border-2 border-[#f59e0b] flex items-center justify-center">
                              <Clock className="w-2 h-2 text-[#f59e0b]" />
                            </div>
                            <div className="bg-[#fffbeb] rounded-lg p-4 border border-[#fde68a]">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs text-[#92400e]">
                                      Follow-up #{campaign.emails.length + schedIndex + 1}
                                    </span>
                                    <Badge className="bg-[#fef3c7] text-[#92400e] border-0 text-xs">
                                      <Calendar className="w-3 h-3 mr-1" />
                                      Scheduled
                                    </Badge>
                                  </div>
                                  <p className="font-medium text-[#374151] text-sm">{scheduled.subject}</p>
                                  <p className="text-xs text-[#92400e] mt-1">
                                    Scheduled for: {new Date(scheduled.scheduled_for).toLocaleString()}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    onClick={() => handleSendScheduledNow(scheduled.id)}
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-brand hover:text-brand-dark"
                                    title="Send now"
                                  >
                                    <Play className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    onClick={() => handleCancelScheduled(scheduled.id)}
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0 text-[#dc2626] hover:text-[#b91c1c]"
                                    title="Cancel"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* Cancelled/Sent Scheduled (collapsed) */}
                        {campaign.scheduled.filter(s => s.status !== 'scheduled').length > 0 && (
                          <p className="text-xs text-[#6b7280] pl-8">
                            + {campaign.scheduled.filter(s => s.status !== 'scheduled').length} past scheduled emails
                          </p>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        )}
      </main>

      {/* Follow-up Email Dialog */}
      <Dialog open={showFollowUpDialog} onOpenChange={setShowFollowUpDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-brand-ink">
              Schedule Follow-up for {selectedCampaign?.company_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Generation Type */}
            <div>
              <Label className="text-sm font-medium text-[#374151] mb-3 block">Generation Type</Label>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={followUpType === "new" ? "default" : "outline"}
                  onClick={() => setFollowUpType("new")}
                  className={followUpType === "new" ? "bg-brand text-white" : ""}
                  size="sm"
                >
                  Fresh Angle
                </Button>
                <Button
                  type="button"
                  variant={followUpType === "reference_previous" ? "default" : "outline"}
                  onClick={() => setFollowUpType("reference_previous")}
                  className={followUpType === "reference_previous" ? "bg-brand text-white" : ""}
                  size="sm"
                >
                  Reference Previous
                </Button>
              </div>
              <p className="text-xs text-[#6b7280] mt-2">
                {followUpType === "new" 
                  ? "Generate a follow-up with a completely new angle and insights"
                  : "Generate a follow-up that references your previous email"}
              </p>
            </div>

            {/* Generate Button */}
            <Button
              onClick={handleGenerateFollowUp}
              disabled={generatingFollowUp}
              variant="outline"
              className="w-full border-brand text-brand hover:bg-brand hover:text-white"
            >
              {generatingFollowUp ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating Follow-up...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Generate Follow-up Email
                </>
              )}
            </Button>

            {/* Subject Line */}
            <div>
              <Label htmlFor="followup-subject" className="text-sm font-medium text-[#374151] mb-2">Subject Line</Label>
              <Input
                id="followup-subject"
                value={followUpSubject}
                onChange={(e) => setFollowUpSubject(e.target.value)}
                placeholder="Follow-up subject line"
              />
            </div>

            {/* Email Body */}
            <div>
              <Label htmlFor="followup-body" className="text-sm font-medium text-[#374151] mb-2">Email Body</Label>
              <Textarea
                id="followup-body"
                value={followUpBody}
                onChange={(e) => setFollowUpBody(e.target.value)}
                placeholder="Follow-up email content"
                rows={8}
                className="text-sm"
              />
            </div>

            {/* Schedule Options */}
            <div>
              <Label className="text-sm font-medium text-[#374151] mb-3 block">Schedule Time</Label>
              
              {/* Quick Presets */}
              <div className="flex flex-wrap gap-2 mb-4">
                <Button type="button" variant="outline" size="sm" onClick={() => setQuickSchedule(2)} className="text-xs">
                  In 2 Days
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setQuickSchedule(3)} className="text-xs">
                  In 3 Days
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setQuickSchedule(5)} className="text-xs">
                  In 5 Days
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setQuickSchedule(7)} className="text-xs">
                  In 1 Week
                </Button>
              </div>

              {/* Custom Date/Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="followup-date" className="text-xs text-[#6b7280] mb-1 block">Date</Label>
                  <Input
                    id="followup-date"
                    type="date"
                    value={followUpScheduleDate}
                    onChange={(e) => setFollowUpScheduleDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <Label htmlFor="followup-time" className="text-xs text-[#6b7280] mb-1 block">Time</Label>
                  <Input
                    id="followup-time"
                    type="time"
                    value={followUpScheduleTime}
                    onChange={(e) => setFollowUpScheduleTime(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Attach PDF Toggle */}
            <div className="flex items-center justify-between p-3 bg-[#f8fafc] rounded-lg border border-[#e2e8f0]">
              <div className="flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-brand" />
                <span className="text-sm text-[#374151]">Attach PDF Report</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={followUpAttachPdf}
                  onChange={(e) => setFollowUpAttachPdf(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brand"></div>
              </label>
            </div>

            {/* Recipient Info */}
            <div className="p-3 bg-[#f0f9ff] rounded-lg border border-[#bae6fd]">
              <p className="text-xs text-[#0369a1]">
                <strong>Recipient:</strong> {selectedCampaign?.emails?.[0]?.to_email || "No recipient found"}
              </p>
            </div>
          </div>

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowFollowUpDialog(false);
                resetFollowUpForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleScheduleFollowUp}
              disabled={schedulingFollowUp || !followUpSubject || !followUpBody || !followUpScheduleDate || !followUpScheduleTime}
              className="bg-brand hover:bg-brand-dark text-white"
            >
              {schedulingFollowUp ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Scheduling...
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Follow-up
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
